/**
 * TermMan.MerchantSetup.LastDatePaidExternal is IRIS %Date (internal integer on the wire).
 * UpdateMerchantSetup assigns JSON without $ZDATEH, so yyyy-mm-dd strings fail validation (7207/5802).
 * This helper rewrites the outbound PUT body using the value returned by GET when the form still
 * holds an ISO-like display string. If the user clears the field, the property is omitted.
 *
 * Limitation: if GET returned a numeric day count and the user edits the control to a *different*
 * calendar date as ISO text, we still resend the original number until the API accepts strings or
 * the backend converts with $ZDATEH.
 */

const ISO_LIKE_DATE = /^\d{4}-\d{2}-\d{2}/;

function coerceLdpeFormValueToComparableString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  return String(value).trim();
}

function parseNumericFromGet(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (/^\d+$/.test(t)) {
      const n = Number(t);
      return Number.isFinite(n) ? n : undefined;
    }
  }
  return undefined;
}

export function applyMerchantSetupLastDatePaidExternalForPut(
  body: Record<string, unknown>,
  rawLastDatePaidExternalFromGet: unknown,
): void {
  const formVal = body['lastDatePaidExternal'];
  const formStr = coerceLdpeFormValueToComparableString(formVal);

  if (formStr === '') {
    delete body['lastDatePaidExternal'];
    return;
  }

  const numericFromGet = parseNumericFromGet(rawLastDatePaidExternalFromGet);

  if (numericFromGet !== undefined) {
    if (ISO_LIKE_DATE.test(formStr)) {
      body['lastDatePaidExternal'] = numericFromGet;
      return;
    }
    if (formStr === String(numericFromGet)) {
      body['lastDatePaidExternal'] = numericFromGet;
      return;
    }
    const asNum = Number(formVal);
    if (Number.isFinite(asNum) && formStr === String(asNum)) {
      body['lastDatePaidExternal'] = asNum;
      return;
    }
    body['lastDatePaidExternal'] = numericFromGet;
    return;
  }

  if (typeof formVal === 'string' && ISO_LIKE_DATE.test(formStr)) {
    delete body['lastDatePaidExternal'];
  }
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Added applyMerchantSetupLastDatePaidExternalForPut() for MSE PUT payloads. What changed: New util coerces lastDatePaidExternal on the outbound body when the form holds yyyy-mm-dd (or Date) but GET returned an IRIS %Date integer; clears property when form is empty; drops ISO when no numeric baseline from GET. Previous behavior: updateMSE spread form values and sent ISO strings; IRIS rejected with datatype-not-number. New behavior: Merchant setup edit components call this before emit. Reason: PADM/MBE UpdateMerchantSetup does not call $ZDATEH for this field. Impact: MSE save succeeds for typical load-edit-save; see JSDoc limitation if user changes date via ISO only. - 2026-04-15 - HE
